import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { contactInfo } from '../mockData';
import { Phone, Mail, MapPin } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    honeypot: '' // Bot prevention field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Message Sent!', {
          description: data.message || 'Thank you for contacting us. We\'ll get back to you soon.',
        });
        setFormData({ name: '', email: '', phone: '', message: '', honeypot: '' });
      } else {
        toast.error('Error', {
          description: data.detail || 'Failed to send message. Please try again.',
        });
      }
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to send message. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black py-16 px-4">
      <Helmet>
        <title>Contact Kingdom Auto Detailing Tralee | Get In Touch | Co Kerry</title>
        <meta name="description" content="Contact Kingdom Auto Detailing in Kerry. Call 089 653 6680 or email kingdomadbooking@gmail.com. Serving Tralee and Co Kerry. Get a free quote today!" />
        <meta name="keywords" content="contact car detailing Kerry, Kingdom Auto Detailing phone, car valet Tralee contact, auto detailing Kerry email, car wash Kerry location" />
        <link rel="canonical" href="https://kingdomautodetailing.ie/contact" />
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-12 text-center">Contact Us</h1>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-zinc-900 rounded-3xl p-8">
            <h2 className="text-3xl font-bold text-white mb-6">Send us a message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Name *"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 h-12"
                />
              </div>
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email address *"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 h-12"
                />
              </div>
              <div>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 h-12"
                />
              </div>
              <div>
                <Textarea
                  name="message"
                  placeholder="Message *"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold h-12 text-lg"
              >
                {isSubmitting ? 'Sending...' : 'Submit form'}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-white mb-6">Location</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="text-white mt-1" size={24} />
                  <div>
                    <p className="text-white font-semibold">{contactInfo.address}</p>
                    <p className="text-blue-100">{contactInfo.city}</p>
                    {contactInfo.state && <p className="text-blue-100">{contactInfo.state}</p>}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-white mb-6">Get in touch</h2>
              <div className="space-y-4">
                <a
                  href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-center space-x-3 text-white hover:text-lime-500 transition-colors group"
                >
                  <Phone size={24} className="group-hover:scale-110 transition-transform" />
                  <span className="text-lg font-semibold">{contactInfo.phone}</span>
                </a>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center space-x-3 text-white hover:text-lime-500 transition-colors group"
                >
                  <Mail size={24} className="group-hover:scale-110 transition-transform" />
                  <span className="text-lg">{contactInfo.email}</span>
                </a>
              </div>
            </div>

            <div className="bg-zinc-900 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Business Hours</h3>
              <div className="space-y-2 text-gray-300">
                <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                <p>Saturday: 9:00 AM - 4:00 PM</p>
                <p>Sunday: By Appointment</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;